import { UseFormGetValues, UseFormReset, UseFormSetValue } from 'react-hook-form'
import {
  Device,
  EebusChargingStation,
  Emc,
  HeatpumpIo,
  Htc320hIo,
  InstallationFormModel,
  ModbusTcpChargingStation,
  MyPvIo,
  Storage,
  StorageIo,
} from '../../forms'
import { getDeviceDefaultName } from '../../../parsers'
import { deleteDeviceLink } from '../device-link'
import { deleteEvse, deleteIo, deleteMeter } from '../sub-devices'
import { saveStorageSettingsForRestore } from './save-storage-settings'
import { TFunction } from 'i18next'
import { DeleteRequest, InstallationDeleteDialogProps } from 'src/Libs/flow/advanced-settings/components'
import { getUuid, logError } from 'src/Libs/flow/utils'
import { AuthFetchWithResponse } from 'src/Types/api'
import { Meter, MeterDeviceType } from 'src/Libs/flow/advanced-settings/powermeter'

const getDeviceDeleteMessage = (device: Device, t: TFunction): string => {
  const { name, deviceId, deviceCategory: category } = device

  const displayName = getDeviceDefaultName(name, category, deviceId, t)

  if (category === 'EMC') return t('installation.types.emc')
  if (category === 'Storage') return `${t('installation.types.storage')} ${displayName}`
  if (category === 'ModbusTCP Evse' || category === 'EEBus Evse') return `${t('installation.types.Evse')} ${displayName}`
  if (category === 'Meter') {
    const meter = device as Meter
    const meterType: Record<MeterDeviceType, string> = {
      HVAC: t('installation.submeterEditor.deviceTypeOptions.hvac'),
      PVExtern: t('installation.submeterEditor.deviceTypeOptions.pv'),
      Evse: t('installation.submeterEditor.deviceTypeOptions.evse'),
      House: t('installation.submeterEditor.deviceTypeOptions.house'),
    }
    return `${t('installation.types.submeter')} ${meterType[meter.deviceType]} ${displayName}`
  }
  if (category === 'HTC320H I/O') return `${t('installation.types.modbusRTU')} ${displayName}`
  if (category === 'Storage I/O') return `${t('installation.types.IOController')} ${displayName}`
  if (category === 'MyPV I/O') return `${t('installation.types.modbusTCP')} ${displayName}`
  if (category === 'Heatpump') return `${t('installation.heatpumpEditor.emptyTitle')} ${displayName}`

  logError('Device type did not correspond to a deletion message.')
  return ''
}

export const deleteIndividualDevice = (
  device: Device,
  getValues: UseFormGetValues<InstallationFormModel>,
  setValue: UseFormSetValue<InstallationFormModel>,
  openInstallationDeleteDialog: (props: InstallationDeleteDialogProps) => void,
  reset: UseFormReset<InstallationFormModel>,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): void => {
  if (!device.deviceId) {
    removeUnsavedDeviceFromInstallation(device, getValues, setValue)
    return
  }

  const installationId = getValues('id')

  if (!installationId) {
    logError('No installation ID. Can not delete device.')
    return
  }

  const deleteRequest = deleteSavedDeviceFromInstallation(device, getValues, t, authFetch)

  if (!deleteRequest) return

  openInstallationDeleteDialog({
    installationId,
    requests: [deleteRequest],
    reset,
    shouldSync: !!getValues('emcDevice.pairingId'),
  })
}

export const removeUnsavedDeviceFromInstallation = (
  device: Device,
  getValues: UseFormGetValues<InstallationFormModel>,
  setValue: UseFormSetValue<InstallationFormModel>
): void => {
  const { deviceCategory: category } = device

  if (category === 'EMC') return removeEmcFromInstallation(setValue)
  if (category === 'Storage') return removeStorageFromInstallation(device.id, getValues, setValue)
  if (category === 'ModbusTCP Evse') return removeModbusTcpFromInstallation(device, getValues, setValue)
  if (category === 'EEBus Evse') return removeEebusFromInstallation(device, getValues, setValue)
  if (category === 'Heatpump') return removeHeatpumpFromInstallation(setValue)
  if (category === 'Meter') return removeMeterFromInstallation(device, getValues, setValue)
  if (category === 'HTC320H I/O') return removeHtc320hIoFromInstallation(setValue)
  if (category === 'Storage I/O') return removeStorageIoFromInstallation(setValue)
  if (category === 'MyPV I/O') return removeMyPvIoFromInstallation(setValue)

  logError('No device type was found for deletion.')
  return
}

export const deleteSavedDeviceFromInstallation = (
  device: Device,
  getValues: UseFormGetValues<InstallationFormModel>,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): DeleteRequest | null => {
  const installationId = getValues('id')
  if (installationId === null) {
    logError('Installation id is null. Can not delete device')
    return null
  }

  const { deviceCategory: category, deviceId } = device

  if (!deviceId) {
    logError(`${category} has no ID. Can not delete device.`)
    return null
  }

  if (category === 'EMC') return emcDeleteRequest(installationId, device as Emc, t, authFetch)
  if (category === 'Storage') return storageDeleteRequest(installationId, device as Storage, getValues, t, authFetch)

  const emcId = getValues('emcDevice.deviceId') || ''
  if (!emcId) {
    logError('EMC has no ID. Can not delete device.')
    return null
  }
  // Extra handling is needed for Storage I/O, because the deviceLinkId is needed for deletion instead of the emcId.
  const storageIoDevice = device as StorageIo

  if (category === 'ModbusTCP Evse') return modbusTcpDeleteRequest(installationId, emcId, device as ModbusTcpChargingStation, t, authFetch)
  if (category === 'EEBus Evse') return eebusDeleteRequest(installationId, emcId, device as EebusChargingStation, t, authFetch)

  if (category === 'Heatpump') return heatpumpDeleteRequest(installationId, emcId, device as HeatpumpIo, t, authFetch)
  if (category === 'Meter') return meterDeleteRequest(installationId, emcId, device as Meter, t, authFetch)
  if (category === 'HTC320H I/O') return htc320hIoDeleteRequest(installationId, emcId, device as Htc320hIo, t, authFetch)
  if (category === 'Storage I/O') return storageIoDeleteRequest(installationId, emcId, storageIoDevice, t, authFetch)
  if (category === 'MyPV I/O') return MyPvIoDeleteRequest(installationId, emcId, device as MyPvIo, t, authFetch)

  logError('No device type was found for deletion.')
  return null
}

const removeEmcFromInstallation = (setValue: UseFormSetValue<InstallationFormModel>): void => {
  setValue('emcDevice', null, {
    shouldDirty: true,
  })
}
const emcDeleteRequest = (installationId: number, emc: Emc, t: TFunction, authFetch: AuthFetchWithResponse): DeleteRequest | null => {
  const deviceId = emc.deviceId
  return {
    uid: getUuid(),
    message: getDeviceDeleteMessage(emc, t),
    request: () => deleteDeviceLink(installationId, deviceId, authFetch),
  }
}

const removeStorageFromInstallation = (
  deviceFormId: string,
  getValues: UseFormGetValues<InstallationFormModel>,
  setValue: UseFormSetValue<InstallationFormModel>
): void => {
  // Save storage system values for specific installation in session storage
  // to be able to restore them if an new storage is added.
  const installationId = getValues('id')
  saveStorageSettingsForRestore(installationId, getValues('storageSystem'))

  setValue(
    'storageDevices',
    getValues('storageDevices').filter(storageDevice => storageDevice.id !== deviceFormId),
    {
      shouldDirty: true,
    }
  )
}
const storageDeleteRequest = (
  installationId: number,
  storage: Storage,
  getValues: UseFormGetValues<InstallationFormModel>,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): DeleteRequest | null => {
  // Save storage system values for specific installation in session storage
  // to be able to restore them if an new storage is added.
  saveStorageSettingsForRestore(installationId, getValues('storageSystem'))

  return {
    uid: getUuid(),
    message: getDeviceDeleteMessage(storage, t),
    request: () => deleteDeviceLink(installationId, storage.deviceId, authFetch),
  }
}

const removeModbusTcpFromInstallation = (
  device: Device,
  getValues: UseFormGetValues<InstallationFormModel>,
  setValue: UseFormSetValue<InstallationFormModel>
): void => {
  const modbusTcp = getValues('chargingStations.modbusTcp')
  setValue(
    'chargingStations.modbusTcp',
    modbusTcp.filter(modbusTcp => modbusTcp.id !== device.id),
    {
      shouldDirty: true,
    }
  )
}
const modbusTcpDeleteRequest = (
  installationId: number,
  emcId: string,
  modbusTcp: ModbusTcpChargingStation,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): DeleteRequest | null => {
  return {
    uid: getUuid(),
    message: getDeviceDeleteMessage(modbusTcp, t),
    request: () => deleteEvse(installationId, emcId, modbusTcp.deviceId, authFetch),
  }
}

const removeEebusFromInstallation = (
  device: Device,
  getValues: UseFormGetValues<InstallationFormModel>,
  setValue: UseFormSetValue<InstallationFormModel>
): void => {
  const eebus = getValues('chargingStations.eebus')
  setValue(
    'chargingStations.eebus',
    eebus.filter(modbusTcp => modbusTcp.id !== device.id),
    {
      shouldDirty: true,
    }
  )
}
const eebusDeleteRequest = (
  installationId: number,
  emcId: string,
  eebus: EebusChargingStation,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): DeleteRequest | null => {
  return {
    uid: getUuid(),
    message: getDeviceDeleteMessage(eebus, t),
    request: () => deleteEvse(installationId, emcId, eebus.deviceId, authFetch),
  }
}

const removeHeatpumpFromInstallation = (setValue: UseFormSetValue<InstallationFormModel>): void => {
  setValue('heatpump', null, {
    shouldDirty: true,
  })
}
const heatpumpDeleteRequest = (
  installationId: number,
  emcId: string,
  heatpump: HeatpumpIo,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): DeleteRequest | null => {
  return {
    uid: getUuid(),
    message: getDeviceDeleteMessage(heatpump, t),
    request: () => deleteIo(installationId, emcId, heatpump.deviceId, authFetch),
  }
}

const removeMeterFromInstallation = (
  device: Device,
  getValues: UseFormGetValues<InstallationFormModel>,
  setValue: UseFormSetValue<InstallationFormModel>
): void => {
  const meters = getValues('meters')
  setValue(
    'meters',
    meters.filter(meter => meter.id !== device.id),
    {
      shouldDirty: true,
    }
  )
}
const meterDeleteRequest = (
  installationId: number,
  emcId: string,
  meter: Meter,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): DeleteRequest | null => {
  return {
    uid: getUuid(),
    message: getDeviceDeleteMessage(meter, t),
    request: () => deleteMeter(installationId, emcId, meter.deviceId, authFetch),
  }
}

const removeHtc320hIoFromInstallation = (setValue: UseFormSetValue<InstallationFormModel>): void => {
  setValue('ioInterfaces.htc320h', null, {
    shouldDirty: true,
  })
}
const htc320hIoDeleteRequest = (
  installationId: number,
  emcId: string,
  htc320hIo: Htc320hIo,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): DeleteRequest | null => {
  return {
    uid: getUuid(),
    message: getDeviceDeleteMessage(htc320hIo, t),
    request: () => deleteIo(installationId, emcId, htc320hIo.deviceId, authFetch),
  }
}

const removeStorageIoFromInstallation = (setValue: UseFormSetValue<InstallationFormModel>): void => {
  setValue('ioInterfaces.storage', null, {
    shouldDirty: true,
  })
}
const storageIoDeleteRequest = (
  installationId: number,
  emcId: string,
  storageIo: StorageIo,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): DeleteRequest | null => {
  return {
    uid: getUuid(),
    message: getDeviceDeleteMessage(storageIo, t),
    request: () => deleteIo(installationId, emcId, storageIo.deviceId, authFetch),
  }
}

const removeMyPvIoFromInstallation = (setValue: UseFormSetValue<InstallationFormModel>): void => {
  setValue('ioInterfaces.mypv', null, {
    shouldDirty: true,
  })
}
const MyPvIoDeleteRequest = (
  installationId: number,
  emcId: string,
  myPv: MyPvIo,
  t: TFunction,
  authFetch: AuthFetchWithResponse
): DeleteRequest | null => {
  return {
    uid: getUuid(),
    message: getDeviceDeleteMessage(myPv, t),
    request: () => deleteIo(installationId, emcId, myPv.deviceId, authFetch),
  }
}
